import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Mail, Lock, User, ArrowLeft, Building2, Palette, CheckCircle, Heart } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['auth', 'common']);
  const { login, register, googleLogin, isAuthenticated, user } = useAuth();
  
  const [isLogin, setIsLogin] = useState(true);
  const [userType, setUserType] = useState(searchParams.get("type") || "business");
  const [referralCode, setReferralCode] = useState(searchParams.get("ref") || "");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.user_type === "business") {
        navigate("/dashboard");
      } else if (user.user_type === "creator") {
        navigate("/creator");
      } else {
        navigate("/admin");
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        if (!formData.name.trim()) {
          setError(t('auth:errors.nameRequired'));
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name, userType);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('auth:errors.authFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (referralCode) {
      localStorage.setItem("pending_referral_code", referralCode);
    }
    googleLogin(userType);
  };

  // Get benefits array from translations
  const benefits = t(`auth:${userType}.benefits`, { returnObjects: true });

  return (
    <div className="min-h-screen bg-[#05060D] flex relative">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl"></div>
      </div>
      
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-cyan-600/10"></div>
        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="flex items-center gap-2 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
              alt="EleKtro"
              className="w-10 h-10"
            />
            <div className="flex flex-col">
              <span className="text-xl font-bold text-white leading-tight">EleKtro Universal</span>
              <span className="text-[10px] text-gray-500 leading-tight">Conscious Creator</span>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold font-['Unbounded'] text-white mb-6">
            {t(`auth:${userType}.headline`)}<br />
            <span className="text-cyan-400">{t(`auth:${userType}.headlineHighlight`)}</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            {t(`auth:${userType}.description`)}
          </p>
          
          <div className="space-y-4">
            {Array.isArray(benefits) && benefits.map((item, index) => (
              <div key={`benefit-${index}`} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-cyan-400" />
                <span className="text-gray-300">{item}</span>
              </div>
            ))}
          </div>
          
          {/* Results-based earnings note for creators */}
          {userType === "creator" && (
            <p className="mt-6 text-sm text-gray-400">
              {t('auth:creator.earningsNote')}
            </p>
          )}
          
          {/* Logo */}
          <div className="mt-12 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
              alt="EleKtro"
              className="w-40 h-40 opacity-50"
            />
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md">
          {/* Header with Back button and Language Switcher */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('auth:nav.backToHome')}
            </button>
            <LanguageSwitcher />
          </div>

          {/* Referral Badge */}
          {referralCode && !isLogin && (
            <div className="mb-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center gap-3">
              <Heart className="w-5 h-5 text-purple-400 fill-purple-400" />
              <div>
                <div className="text-sm font-medium text-purple-400">{t('auth:referral.applied')}</div>
                <div className="text-xs text-gray-400">{t('auth:referral.bonus')}</div>
              </div>
            </div>
          )}

          {/* User Type Toggle */}
          <div className="flex gap-2 mb-8 p-1 bg-white/5 rounded-xl">
            <button
              onClick={() => setUserType("creator")}
              data-testid="auth-type-creator"
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold transition-all ${
                userType === "creator"
                  ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              <Palette className="w-4 h-4" />
              {t('auth:creator.title')}
            </button>
            <button
              onClick={() => setUserType("business")}
              data-testid="auth-type-business"
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold transition-all ${
                userType === "business"
                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              <Building2 className="w-4 h-4" />
              {t('auth:business.title')}
            </button>
          </div>

          <div className="glass-card rounded-2xl p-8">
            <h2 className="text-2xl font-bold font-['Unbounded'] mb-2">
              {isLogin ? t('auth:login.title') : t('auth:register.title')}
            </h2>
            <p className="text-gray-400 mb-6">
              {isLogin 
                ? t('auth:login.subtitle')
                : t('auth:register.subtitle')}
            </p>

            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('auth:register.name')}
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      data-testid="auth-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400"
                      placeholder={userType === "business" 
                        ? t('auth:register.businessNamePlaceholder') 
                        : t('auth:register.namePlaceholder')}
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth:login.email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    data-testid="auth-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth:login.password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="password"
                    data-testid="auth-password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="auth-submit-btn"
                disabled={loading}
                className="w-full btn-primary py-4 rounded-xl font-bold text-white disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {t('common:actions.loading')}
                  </span>
                ) : isLogin ? t('auth:login.submit') : t('auth:register.submit')}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#0A0B14] text-gray-500">{t('auth:google.or')}</span>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              data-testid="auth-google-btn"
              className="w-full py-4 rounded-xl font-semibold border border-white/20 text-white hover:bg-white/5 transition-all flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth:google.continueWith')}
            </button>

            <p className="mt-6 text-center text-gray-400">
              {isLogin ? t('auth:login.noAccount') : t('auth:register.hasAccount')}{" "}
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError("");
                }}
                data-testid="auth-toggle-btn"
                className="text-cyan-400 hover:text-cyan-300 font-semibold"
              >
                {isLogin ? t('auth:login.createAccount') : t('auth:register.signIn')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
